// The two AppBar shapes the Flutter app uses on every screen.
//
// CenterTitleBar — a centred bold title in the 'BetterFonts' family
//   (util.dart appBar_heading): Favourites, Locations, Scenes, Me.
//
// ProfileTitleBar — a dense ListTile: circular profile avatar, bold title,
//   smaller subtitle. Used by Rooms, Devices and every device control screen.
//
// Both sit on BackColor with FontColor icons, elevation 5, black shadow.

import React from 'react';
import {
  AppBar, Toolbar, Typography, Box, IconButton, Avatar, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../../core/state/AppContext';
import { APP_VERSION } from '../../../core/version';

const HEADING_FONT = "'BetterFonts', 'Satisfy', cursive";

// The running version, small and unobtrusive, so anyone can tell at a glance
// which build a phone has. This tracks the BUNDLE, so after an OTA it shows the
// new number — which is how you confirm an OTA actually landed on a device.
function VersionTag() {
  const theme = useTheme();
  if (!APP_VERSION) return null;
  return (
    <Typography
      component="span"
      sx={{
        position: 'absolute',
        right: 8,
        bottom: 2,
        fontSize: 9,
        lineHeight: 1,
        letterSpacing: '0.2px',
        color: theme.sli.text,
        opacity: 0.55,
        pointerEvents: 'none',
      }}
    >
      {`v${APP_VERSION}`}
    </Typography>
  );
}

function useBarSx() {
  const theme = useTheme();
  return {
    bgcolor: theme.sli.back,
    color: theme.sli.text,
    boxShadow: '0 2px 5px rgba(0,0,0,0.6)',
    backgroundImage: 'none',
  };
}

export function CenterTitleBar({ title, actions, showBack = false }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const sx = useBarSx();
  return (
    <AppBar position="sticky" elevation={5} sx={sx}>
      <Toolbar sx={{ position: 'relative' }}>
        {showBack && (
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: theme.sli.text }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography
            sx={{
              color: theme.sli.text,
              fontWeight: 900,
              fontFamily: HEADING_FONT,
              fontSize: 20,
            }}
          >
            {title}
          </Typography>
        </Box>
        {actions}
        <VersionTag />
      </Toolbar>
    </AppBar>
  );
}

export function ProfileTitleBar({ title, subtitle, actions, showBack = true }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const { profileUrl } = useApp();
  const sx = useBarSx();

  return (
    <AppBar position="sticky" elevation={5} sx={sx}>
      <Toolbar sx={{ gap: 1, position: 'relative' }}>
        {showBack && (
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ color: theme.sli.text }}>
            <ArrowBackIcon />
          </IconButton>
        )}
        <Avatar src={profileUrl || undefined} sx={{ width: 40, height: 40 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontSize: 18, color: '#fff', fontWeight: 700 }}>
            {title}
          </Typography>
          {subtitle != null && (
            <Typography noWrap sx={{ fontSize: 14, color: '#fff' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
        <VersionTag />
      </Toolbar>
    </AppBar>
  );
}
